import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { MousePointer2, Square, Trash2, Save } from "lucide-react";

/*
 * Annotation colours.
 *
 * These are data, not styling: they are stored on the project and parsed by
 * backend/annotations.py to burn the shape into the exported image, so they
 * have to be literal values the server can read — a CSS custom property would
 * arrive as the string "var(--accent)".
 *
 * They mirror src/styles/tokens.css exactly. The previous list was five Apple
 * system colours including #AF52DE, and "no purple" is a defining rejection of
 * this brand (design_guidelines.json). Keep this list in sync with the tokens.
 */
const COLORS = [
  { value: "#FF3B30", name: "Red" },     // --accent
  { value: "#0022FF", name: "Blue" },    // --accent-2
  { value: "#00803F", name: "Green" },   // --success
  { value: "#FFB800", name: "Amber" },   // --warning
  { value: "#050505", name: "Ink" },     // --text
];
const COLOR_VALUES = COLORS.map((c) => c.value);
const colorName = (v) => COLORS.find((c) => c.value === v)?.name || v;

// A click closer than this to the arrow's start is treated as "still picking
// the target", which is what makes both press-drag-release and click-then-click
// produce an arrow.
const MIN_ARROW_LENGTH = 0.02;

/**
 * The rectangle the image actually occupies inside its wrapper.
 *
 * The wrapper is `aspect-video` and the image is `object-contain`, so for any
 * screenshot that is not exactly 16:9 the image is letterboxed and does NOT
 * fill the wrapper. Normalizing a pointer position against the *wrapper* was
 * the bug this function exists to fix: backend/annotations.py multiplies these
 * coordinates by the real pixel dimensions of the image, so a wrapper-relative
 * 0-1 value silently landed the highlight somewhere else — or in the letterbox
 * — in the rendered video. The SVG preview was anchored to the same wrong box,
 * which is why the editor looked correct and only the export was wrong.
 */
function containRect(box, natural) {
  if (!box || !natural || !natural.w || !natural.h) return null;
  const scale = Math.min(box.width / natural.w, box.height / natural.h);
  const width = natural.w * scale;
  const height = natural.h * scale;
  return {
    left: (box.width - width) / 2,
    top: (box.height - height) / 2,
    width,
    height,
  };
}

function normFromEvent(e, wrapEl, content) {
  const wrap = wrapEl.getBoundingClientRect();
  // content is wrapper-relative; shift it into viewport space to compare
  // against the pointer.
  const left = wrap.left + content.left;
  const top = wrap.top + content.top;
  return {
    x: Math.max(0, Math.min(1, (e.clientX - left) / content.width)),
    y: Math.max(0, Math.min(1, (e.clientY - top) / content.height)),
  };
}

/** Drags that run right-to-left or bottom-to-top produce negative extents. */
function normalizeRect(a, b) {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    w: Math.abs(b.x - a.x),
    h: Math.abs(b.y - a.y),
  };
}

/**
 * Editor for highlight boxes and pointer arrows on a single screenshot.
 * Coordinates are normalized 0–1 relative to the IMAGE (not its wrapper),
 * which is the basis backend/annotations.py expects.
 */
export default function ScreenshotAnnotator({
  screenshotUrl,
  annotations = [],
  onChange,
  onSave,
  readOnly = false,
  saving = false,
}) {
  const wrapRef = useRef(null);
  const [mode, setMode] = useState("highlight"); // highlight | pointer
  const [color, setColor] = useState(COLOR_VALUES[0]);
  const [draft, setDraft] = useState(null);
  const [dragStart, setDragStart] = useState(null);
  const [pointerStart, setPointerStart] = useState(null);
  const [natural, setNatural] = useState(null);
  const [box, setBox] = useState(null);

  // Wrapper size drives the content rect, so it has to be tracked, not
  // measured once — the panel is inside a responsive grid.
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setBox({ width: r.width, height: r.height });
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    setDraft(null);
    setDragStart(null);
    setPointerStart(null);
    setNatural(null);
  }, [screenshotUrl]);

  const content = containRect(box, natural);
  // Until the image reports its intrinsic size we cannot map a click onto it.
  // Drawing against the wrapper instead is exactly the defect being fixed, so
  // input stays disabled for those few milliseconds.
  const ready = Boolean(content) && !readOnly;

  const onImgLoad = useCallback((e) => {
    const img = e.currentTarget;
    if (img.naturalWidth && img.naturalHeight) {
      setNatural({ w: img.naturalWidth, h: img.naturalHeight });
    }
  }, []);

  const handlePointerDown = (e) => {
    if (!ready) return;
    const p = normFromEvent(e, wrapRef.current, content);
    if (mode === "highlight") {
      setDragStart(p);
      setDraft({ type: "highlight", x: p.x, y: p.y, w: 0, h: 0, color });
    } else if (!pointerStart) {
      setPointerStart(p);
    }
  };

  const handlePointerMove = (e) => {
    if (!ready) return;
    const p = normFromEvent(e, wrapRef.current, content);
    if (mode === "highlight" && dragStart) {
      setDraft({ type: "highlight", ...normalizeRect(dragStart, p), color });
    } else if (mode === "pointer" && pointerStart) {
      // Live preview of the arrow while the target is being chosen.
      setDraft({
        type: "pointer",
        from_x: pointerStart.x, from_y: pointerStart.y,
        to_x: p.x, to_y: p.y,
        color,
      });
    }
  };

  const handlePointerUp = (e) => {
    if (!ready) return;
    const p = normFromEvent(e, wrapRef.current, content);

    if (mode === "highlight") {
      if (dragStart) {
        const rect = normalizeRect(dragStart, p);
        if (rect.w >= 0.01 && rect.h >= 0.01) {
          onChange([...annotations, { type: "highlight", ...rect, color }]);
        }
      }
      setDragStart(null);
      setDraft(null);
      return;
    }

    if (mode === "pointer" && pointerStart) {
      const far = Math.hypot(p.x - pointerStart.x, p.y - pointerStart.y) >= MIN_ARROW_LENGTH;
      // A release on (or very near) the start point means the user clicked to
      // place the start and is now choosing the target — keep waiting. Without
      // this, the pointerup that follows the very first pointerdown committed a
      // zero-length arrow, so the documented click-then-click flow was
      // impossible and only press-drag-release worked.
      if (!far) return;
      onChange([
        ...annotations,
        {
          type: "pointer",
          from_x: pointerStart.x,
          from_y: pointerStart.y,
          to_x: p.x,
          to_y: p.y,
          color,
        },
      ]);
      setPointerStart(null);
      setDraft(null);
    }
  };

  const cancelDraft = () => {
    setDraft(null);
    setDragStart(null);
    setPointerStart(null);
  };

  const removeAt = (idx) => {
    onChange(annotations.filter((_, i) => i !== idx));
  };

  const renderAnn = (ann, key, opacity = 1) => {
    const c = ann.color || COLOR_VALUES[0];
    if (ann.type === "highlight") {
      return (
        <rect
          key={key}
          x={`${ann.x * 100}%`}
          y={`${ann.y * 100}%`}
          width={`${ann.w * 100}%`}
          height={`${ann.h * 100}%`}
          fill={c}
          fillOpacity={0.25 * opacity}
          stroke={c}
          strokeWidth={2}
        />
      );
    }
    if (ann.type === "pointer") {
      const fx = ann.from_x * 100;
      const fy = ann.from_y * 100;
      const tx = ann.to_x * 100;
      const ty = ann.to_y * 100;
      return (
        <g key={key} opacity={opacity}>
          <line x1={`${fx}%`} y1={`${fy}%`} x2={`${tx}%`} y2={`${ty}%`} stroke={c} strokeWidth={3} />
          <circle cx={`${tx}%`} cy={`${ty}%`} r={6} fill={c} />
          <circle cx={`${fx}%`} cy={`${fy}%`} r={4} fill={c} />
        </g>
      );
    }
    return null;
  };

  const hint = readOnly
    ? `${annotations.length} annotation(s) on this slide`
    : !content
      ? "Loading screenshot…"
      : mode === "highlight"
        ? "Drag to draw a highlight box"
        : pointerStart
          ? "Now click the target — or press Escape to cancel"
          : "Click start point, then click target for the pointer arrow";

  return (
    <div className="space-y-3" data-testid="screenshot-annotator">
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className={`brutal-btn ${mode === "highlight" ? "" : "brutal-btn-secondary"}`}
            onClick={() => { setMode("highlight"); cancelDraft(); }}
            aria-pressed={mode === "highlight"}
            data-testid="ann-mode-highlight"
          >
            <Square size={14} aria-hidden="true"/> Highlight
          </button>
          <button
            type="button"
            className={`brutal-btn ${mode === "pointer" ? "" : "brutal-btn-secondary"}`}
            onClick={() => { setMode("pointer"); cancelDraft(); }}
            aria-pressed={mode === "pointer"}
            data-testid="ann-mode-pointer"
          >
            <MousePointer2 size={14} aria-hidden="true"/> Pointer
          </button>
          <div className="flex gap-1 ml-2" role="group" aria-label="Annotation colour">
            {COLORS.map((c) => (
              <button
                key={c.value}
                type="button"
                aria-label={c.name}
                aria-pressed={color === c.value}
                onClick={() => setColor(c.value)}
                className={`w-6 h-6 brutal-border ${color === c.value ? "ring-offset-0 outline outline-2 outline-offset-2 outline-rule" : ""}`}
                style={{ background: c.value }}
                data-testid={`ann-color-${c.name.toLowerCase()}`}
              />
            ))}
          </div>
          {onSave && (
            <button
              type="button"
              className="brutal-btn brutal-btn-secondary ml-auto"
              onClick={onSave}
              disabled={saving}
              data-testid="ann-save"
            >
              <Save size={14} aria-hidden="true"/> {saving ? "Saving…" : "Save annotations"}
            </button>
          )}
        </div>
      )}

      <div
        ref={wrapRef}
        className={`relative brutal-border aspect-video overflow-hidden bg-surface-inverse ${ready ? "cursor-crosshair" : ""}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={() => { if (mode === "highlight") { setDraft(null); setDragStart(null); } }}
        onKeyDown={(e) => { if (e.key === "Escape") cancelDraft(); }}
        data-testid="ann-canvas"
      >
        <img
          src={screenshotUrl}
          onLoad={onImgLoad}
          alt="Screenshot being annotated"
          className="w-full h-full object-contain select-none pointer-events-none"
          draggable={false}
        />
        {/* Anchored to the image's own box, not the wrapper, so what is drawn
            here is exactly what the server burns into the export. */}
        {content && (
          <svg
            className="absolute pointer-events-none"
            style={{ left: content.left, top: content.top, width: content.width, height: content.height }}
            aria-hidden="true"
          >
            {annotations.map((ann, i) => renderAnn(ann, `a-${i}`))}
            {draft && renderAnn(draft, "draft", 0.7)}
            {pointerStart && mode === "pointer" && (
              <circle cx={`${pointerStart.x * 100}%`} cy={`${pointerStart.y * 100}%`} r={5} fill={color} opacity={0.8} />
            )}
          </svg>
        )}
      </div>

      <div className="text-xs uppercase tracking-widest text-ink-muted" role="status" aria-live="polite">
        {hint}
      </div>

      {!readOnly && annotations.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {annotations.map((ann, i) => (
            <button
              key={`chip-${i}`}
              type="button"
              className="brutal-btn brutal-btn-ghost text-xs"
              onClick={() => removeAt(i)}
              aria-label={`Remove ${ann.type} ${i + 1}`}
              data-testid={`ann-remove-${i}`}
            >
              <Trash2 size={10} aria-hidden="true"/> {ann.type} #{i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
